import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  groupByPlace,
  typeLabel,
  type IndexedLocation,
  type StateGroup,
} from '@/utils/locationHubs';

/*
 * Plain links to every location page, grouped by country and US state
 * (ERI-2853). The card grid above it shows 48 locations and loads the rest
 * with a button, which search engines never press; this list is in the
 * prerendered HTML for all of them.
 */

const NAME_LINK = 'text-foreground/80 hover:text-[#c9922a] hover:underline';
const PLACE_LINK = 'text-foreground hover:text-[#c9922a] hover:underline';

function locationPath(slug: string): string {
  return `/dark-sky-locations/${slug}`;
}

/** Location names as links, one per line. */
export const LocationLinkList: React.FC<{ locations: IndexedLocation[]; className?: string }> = ({
  locations,
  className,
}) => (
  <ul className={cn('text-sm leading-relaxed', className)}>
    {locations.map((location) => (
      <li key={location.slug} className="break-inside-avoid py-0.5">
        <Link to={locationPath(location.slug)} className={NAME_LINK}>
          {location.name}
        </Link>
      </li>
    ))}
  </ul>
);

/** Locations with what each one is and its Bortle class, for a hub page. */
export const LocationDetailList: React.FC<{ locations: IndexedLocation[] }> = ({ locations }) => (
  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
    {locations.map((location) => {
      const label = typeLabel(location.type);
      return (
        <li key={location.slug}>
          <Link to={locationPath(location.slug)} className={cn(PLACE_LINK, 'font-medium leading-snug')}>
            {location.name}
          </Link>
          <p className="text-xs text-muted-foreground mt-0.5">
            {label.charAt(0).toUpperCase() + label.slice(1)}
            {location.bortle_class ? ` · Bortle ${location.bortle_class}` : ''}
          </p>
        </li>
      );
    })}
  </ul>
);

/** US states in columns, each heading linking to the state's hub page. */
export const StateColumns: React.FC<{
  states: StateGroup[];
  unplaced: IndexedLocation[];
  headingLevel: 'h2' | 'h4';
}> = ({ states, unplaced, headingLevel: Heading }) => (
  <div className="columns-1 sm:columns-2 lg:columns-3 gap-8">
    {states.map((state) => (
      <div key={state.path} className="break-inside-avoid mb-6">
        <Heading className="text-base font-semibold text-foreground mb-1">
          <Link to={state.path} className={PLACE_LINK}>
            {state.name}
          </Link>{' '}
          <span className="text-sm font-normal text-muted-foreground">({state.locations.length})</span>
        </Heading>
        <LocationLinkList locations={state.locations} />
      </div>
    ))}
    {unplaced.length > 0 && (
      <div className="break-inside-avoid mb-6">
        <Heading className="text-base font-semibold text-foreground mb-1">
          Other United States locations{' '}
          <span className="text-sm font-normal text-muted-foreground">({unplaced.length})</span>
        </Heading>
        <LocationLinkList locations={unplaced} />
      </div>
    )}
  </div>
);

/** Every location, by country and US state, for the /dark-sky-locations page. */
export const LocationIndex: React.FC<{ locations: IndexedLocation[] }> = ({ locations }) => {
  const countries = groupByPlace(locations);
  return (
    <section id="all-locations" aria-labelledby="all-locations-heading" className="mt-16 border-t border-border pt-10 scroll-mt-24">
      <h2 id="all-locations-heading" className="font-serif text-2xl font-bold text-foreground mb-2">All dark sky locations</h2>
      <p className="text-sm text-muted-foreground mb-8">
        All {locations.length} locations in {countries.length} countries. Each country and US state has its own page.
      </p>
      <div className="space-y-10">
        {countries.map((country) => (
          <div key={country.path}>
            <h3 className="text-lg font-semibold text-foreground mb-3">
              <Link to={country.path} className={PLACE_LINK}>
                {country.name}
              </Link>{' '}
              <span className="text-sm font-normal text-muted-foreground">({country.locations.length})</span>
            </h3>
            {country.states.length > 0 ? (
              <StateColumns states={country.states} unplaced={country.unplaced} headingLevel="h4" />
            ) : (
              <LocationLinkList locations={country.locations} className="columns-1 sm:columns-2 lg:columns-3 gap-8" />
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
